import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Layers } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { useApplicationMutations, useApplicationsOverview } from "../../hooks";
import type { BulkCreateApplicationsResult } from "../../types";
import { BulkFormatkiAction } from "./BulkFormatkiAction";
import { InfoHint } from "./InfoHint";

/** Polska odmiana: 1 wniosek, 2–4 wnioski, 5+ wniosków (i 12–14 też „wniosków"). */
const wnioskiLabel = (n: number) => {
  if (n === 1) return "wniosek";
  const last = n % 10;
  const teens = n % 100;
  return last >= 2 && last <= 4 && (teens < 12 || teens > 14) ? "wnioski" : "wniosków";
};

const SKIP_REASONS: Record<string, string> = {
  existing: "ma już wniosek",
  existing_case: "sprawa z urzędem prowadzona w Korespondencji",
  no_parcels: "brak działek na bieżącej trasie",
  unknown: "nie jest już właścicielem działek projektu",
};

/**
 * Komplet wniosków lokalizacyjnych za jednym zamachem: po JEDNYM na właściciela
 * instytucjonalnego. Działka z kilkoma współwłaścicielami wchodzi w zakres wniosku
 * KAŻDEGO z nich — każdy zgadza się osobno, dokładnie jak przy zgodach prywatnych,
 * gdzie działka z N współwłaścicielami dostaje N osobnych WNU.
 *
 * Ekran świadomie nie przeskakuje po zatwierdzeniu na stronę pakietu (pakietów jest
 * kilka) — pokazuje, co powstało, czego nie i co robić dalej.
 */
export function BulkCreateApplicationsModal({
  connectionId,
  onClose,
}: {
  connectionId: string;
  onClose: () => void;
}) {
  const { data: overview } = useApplicationsOverview(connectionId);
  const { bulkCreate } = useApplicationMutations();
  const [result, setResult] = useState<BulkCreateApplicationsResult | null>(null);
  const [error, setError] = useState("");

  const owners = overview?.office_owners ?? [];
  const available = useMemo(
    () => owners.filter((o) => !o.existing_application && !o.existing_case),
    [owners],
  );
  // Domyślnie zaznaczeni wszyscy, którzy czekają na wniosek — po to jest ten ekran.
  const [selected, setSelected] = useState<Set<string> | null>(null);
  const picked = selected ?? new Set(available.map((o) => o.office_id));

  const toggle = (id: string) => {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  // Działki należące do więcej niż jednego z zaznaczonych właścicieli — to one
  // wejdą w zakres kilku wniosków naraz i to o nie pyta zgłoszenie.
  const sharedParcels = useMemo(() => {
    const count = new Map<string, { teryt: string; offices: string[] }>();
    for (const owner of available) {
      if (!picked.has(owner.office_id)) continue;
      for (const parcel of owner.parcels) {
        const row = count.get(parcel.id) ?? { teryt: parcel.teryt, offices: [] };
        row.offices.push(owner.office_name);
        count.set(parcel.id, row);
      }
    }
    return [...count.values()].filter((row) => row.offices.length > 1);
  }, [available, picked]);

  const submit = () => {
    setError("");
    bulkCreate.mutate(
      { connection: connectionId, offices: [...picked] },
      {
        onSuccess: setResult,
        onError: (e: unknown) => {
          const detail = (e as { response?: { data?: { errors?: { detail?: string } } } })
            ?.response?.data?.errors?.detail;
          setError(typeof detail === "string" ? detail : "Nie udało się założyć wniosków.");
        },
      },
    );
  };

  if (result) {
    return (
      <Modal open onClose={onClose} title="Komplet wniosków — wynik" size="lg">
        <div className="space-y-3 text-sm">
          <p className="flex items-start gap-1.5 font-bold">
            <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-green-600" />
            {result.summary}
          </p>

          {result.created.length > 0 && (
            <div>
              <div className="section-label mb-1">Powstałe pakiety ({result.created.length})</div>
              <div className="border border-[var(--color-border)]">
                {result.created.map((row) => (
                  <div
                    key={row.office_id}
                    className="flex items-center gap-2 px-2 py-1 text-xs border-b border-[var(--color-border)] last:border-b-0"
                  >
                    <span className="flex-1 truncate">{row.office_name}</span>
                    <Badge color="gray">{row.parcel_count} dz.</Badge>
                    <Link
                      to={`/power-projects/wnioski/${row.application_id}`}
                      className="font-mono text-[var(--color-primary)] hover:underline"
                    >
                      {row.number}
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.skipped.length > 0 && (
            <div>
              <div className="section-label mb-1">Pominięte ({result.skipped.length})</div>
              <div className="border border-[var(--color-border)]">
                {result.skipped.map((row) => (
                  <div
                    key={row.office_id}
                    className="flex items-center gap-2 px-2 py-1 text-xs border-b border-[var(--color-border)] last:border-b-0"
                  >
                    <span className="flex-1 truncate">{row.office_name}</span>
                    <span className="text-[var(--color-text-muted)]">
                      {SKIP_REASONS[row.reason] ?? row.reason}
                    </span>
                    {row.application_id && (
                      <Link
                        to={`/power-projects/wnioski/${row.application_id}`}
                        className="font-mono text-[var(--color-primary)] hover:underline"
                      >
                        {row.number}
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.created.length > 0 && (
            <div className="border-l-2 border-[var(--color-border-strong)] pl-2 text-xs space-y-1">
              <div className="font-bold">Co dalej</div>
              <p>
                Każdy pakiet prowadzisz osobno przez jego fazy (zakres → pełnomocnictwa
                i opłaty → dokument → mapy → załączniki → kompilacja → wysyłka).
              </p>
              <p>
                <b>Mapy:</b> formatki możesz wygenerować dla całego kompletu naraz —
                każdy wniosek dostanie swój zestaw arkuszy z własną numeracją, na
                szablonie, który wskażesz. Do wydruku spnij te zestawy w jedną
                <b> paczkę</b> w kreatorze formatek: plotujesz i wgrywasz arkusze raz, a
                system rozdzieli je z powrotem do właściwych pakietów.
              </p>
              <div className="pt-0.5">
                <BulkFormatkiAction
                  connectionId={connectionId}
                  applicationIds={result.created.map((row) => row.application_id)}
                  label={`Wygeneruj formatki dla ${result.created.length} ${wnioskiLabel(result.created.length)}`}
                />
              </div>
              <p className="text-[var(--color-text-muted)]">
                Na liście map w pakiecie własna formatka stoi pierwsza i jest oznaczona —
                cudze (z wniosków do innych urzędów, gdy działka jest współwłasna) są
                podpisane numerem wniosku i urzędem.
              </p>
            </div>
          )}

          <div className="flex justify-end">
            <Button onClick={onClose}>Zamknij</Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open onClose={onClose} title="Załóż komplet wniosków lokalizacyjnych" size="lg">
      <div className="space-y-3 text-sm">
        <p className="flex items-start gap-1.5 text-xs text-[var(--color-text-secondary)]">
          <Layers size={14} className="mt-0.5 shrink-0" />
          <span>
            Powstanie <b>jeden wniosek na każdego zaznaczonego właściciela</b>. Działka
            należąca do kilku z nich wejdzie w zakres wniosku każdego — tak samo jak przy
            zgodach prywatnych, gdzie działka z kilkoma współwłaścicielami dostaje osobne
            WNU dla każdego z nich.
          </span>
        </p>

        {!overview ? (
          // Bez tego rozróżnienia ekran w trakcie wczytywania twierdzi, że właścicieli
          // nie ma — i człowiek zamyka go, zanim lista w ogóle dojdzie.
          <div className="text-xs text-[var(--color-text-muted)]">Wczytywanie listy właścicieli…</div>
        ) : owners.length === 0 ? (
          <div className="text-xs text-[var(--color-text-muted)]">
            Wśród działek projektu nie ma właścicieli instytucjonalnych — nie ma dla kogo
            zakładać wniosków.
          </div>
        ) : (
          <div>
            <span className="section-label flex items-center gap-1">
              Adresaci ({picked.size}/{available.length})
              <InfoHint text="Zaznaczeni są wszyscy właściciele instytucjonalni bez wniosku. Ci, którzy już mają aktywny wniosek, są wyszarzeni — reguła „1 urząd = 1 wniosek” jest tu twarda. Świadome założenie drugiego wniosku (błędny szkic, urząd kazał podzielić zakres) zrobisz pojedynczo przyciskiem „Nowy wniosek”." />
            </span>
            <div className="mt-1 max-h-64 overflow-auto border border-[var(--color-border)]">
              {owners.map((o) => {
                // wniosek z modułu Wnioski ALBO sprawa z Korespondencji — oba blokują masówkę
                const taken = !!o.existing_application || !!o.existing_case;
                return (
                  <label
                    key={o.office_id}
                    className={`flex items-center gap-2 px-2 py-1 text-xs border-b border-[var(--color-border)] last:border-b-0 ${
                      taken ? "opacity-60" : "cursor-pointer"
                    }`}
                  >
                    <input
                      type="checkbox"
                      disabled={taken}
                      checked={!taken && picked.has(o.office_id)}
                      onChange={() => toggle(o.office_id)}
                    />
                    <span className="flex-1 truncate">{o.office_name}</span>
                    <Badge color="gray">{o.parcel_count} dz.</Badge>
                    {o.action_parcel_count > 0 && (
                      <Badge color="yellow">{o.action_parcel_count} wymaga akcji</Badge>
                    )}
                    {o.awaiting_parcel_count > 0 && (
                      <Badge color="blue">{o.awaiting_parcel_count} oczekuje na odpowiedź</Badge>
                    )}
                    {o.overdue_parcel_count > 0 && (
                      <Badge color="red">{o.overdue_parcel_count} po terminie KPA</Badge>
                    )}
                    {o.secured_parcel_count > 0 && (
                      <Badge color="green">{o.secured_parcel_count} pozyskane</Badge>
                    )}
                    {taken && (
                      <span className="font-mono text-[var(--color-text-muted)]">
                        {o.existing_application
                          ? `ma już ${o.existing_application.number}`
                          : o.existing_case?.status === "closed"
                            ? "sprawa załatwiona"
                            : "sprawa w Korespondencji"}
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {sharedParcels.length > 0 && (
          <div className="border border-[var(--color-border)] p-2 text-xs space-y-0.5">
            <div className="section-label">
              Działki wspólne — wejdą do kilku wniosków ({sharedParcels.length})
            </div>
            {sharedParcels.slice(0, 6).map((row) => (
              <div key={row.teryt} className="flex gap-2">
                <span className="font-mono">{row.teryt}</span>
                <span className="text-[var(--color-text-muted)] truncate">
                  {row.offices.join(" · ")}
                </span>
              </div>
            ))}
            {sharedParcels.length > 6 && (
              <div className="text-[var(--color-text-muted)]">
                …i {sharedParcels.length - 6} kolejnych
              </div>
            )}
          </div>
        )}

        {picked.size > 0 && (
          <p className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
            <AlertTriangle size={13} className="mt-0.5 shrink-0" />
            <span>
              {picked.size} {wnioskiLabel(picked.size)} = tyle samo osobnych pełnomocnictw
              i opłat skarbowych (17 zł od każdego pełnomocnictwa) oraz osobna
              korespondencja z każdym urzędem.
            </span>
          </p>
        )}

        {error && <div className="text-xs text-red-600">{error}</div>}

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Anuluj</Button>
          <Button
            onClick={submit}
            loading={bulkCreate.isPending}
            disabled={picked.size === 0}
          >
            Załóż {picked.size} {wnioskiLabel(picked.size)}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
